import React from 'react';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import Translate from '@utils/translation-functions';
import Layout from '@layouts/layout';
import SectionLayout from '@layouts/section-layout';
import ErrorLayout from '@layouts/error-layout';
import FlexibleContentHandler from '../design-system/src/components/flexible-content-handler/flexible-content-handler';
import TileBlock from '@blocks/tile-block/tile-block';
import TagList from '@components/tag-list/tag-list';
import Masthead from '@components/masthead/masthead';
import { Paywall } from '@components/paywall/paywall';
import { graphql } from 'gatsby';
import { createNavbarLocaleLinks } from '../utils/generate-url-functions';
import { getPageColour } from '../config/cms-mapping-functions';
import { validateStandardPageData } from '../utils/validate-cms-data-functions';
import { getFooterData } from '../utils/translation-functions';
import { IndentWrapper } from '@components/indent-wrapper/indent-wrapper';
import MailchimpSignUpForm from '@components/mailchimp-sign-up-form/mailchimp-sign-up-form';
import {
  transformFlexibleContent,
  transformTileContent,
  getPodcastEmbedLink,
  transformSocialStaticImages,
  getHrefLangUrls,
} from '../utils/transform-data-functions.js';

const StyledWrapper = styled.div`
  .main-container {
    display: flex;
    max-width: ${vars.mainMaxWidth};
    justify-content: center;
    margin: 0 auto;

    .main-content-container {
      min-width: ${vars.contentMinWidth};
      width: calc(
        ${vars.contentMaxWidth} + calc(2 * ${vars.standardColumnGap})
      );
      padding: ${vars.spacingL} ${vars.mainContentContainerPaddingMobile};

      @media screen and (min-width: ${vars.breakPointTenColumn}) {
        padding: ${vars.spacingL} ${vars.mainContentContainerPaddingTenColumn};
      }

      @media screen and (min-width: ${vars.breakPointTablet}) {
        padding: ${vars.spacingL} ${vars.mainContentContainerPaddingTenColumn};
      }
      @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
        padding: ${vars.spacingL} ${vars.spacingL} 12rem ${vars.spacingL};
      }
    }
  }
`;

export const query = graphql`
  query StandardPageQuery(
    $id: String
    $contentfulId: String
    $podcastSlugs: [String]
  ) {
    standardPage: contentfulStandardPage(id: { eq: $id }) {
      ...StandardPageFields
    }
    allSimplecastPodcastEpisode(filter: { slug: { in: $podcastSlugs } }) {
      nodes {
        id
        slug
      }
    }
    pageDefaultImages: contentfulStandardPage(
      contentful_id: { eq: $contentfulId }
      node_locale: { eq: "en-GB" }
    ) {
      mastheadImage {
        gatsbyImageData(
          placeholder: BLURRED
          formats: [AUTO, AVIF, WEBP]
          quality: 80
        )
        description
        file {
          url
          contentType
        }
      }
      socialShareImage {
        description
        file {
          url
          contentType
        }
      }
    }
    contentfulGlossaryManager(enabled: { eq: true }) {
      termsForTooltip {
        label
        definition {
          definition
        }
        definitionImage {
          gatsbyImageData(
            placeholder: BLURRED
            formats: [AUTO, AVIF, WEBP]
            quality: 80
          )
          description
          file {
            url
            contentType
          }
        }
        linkType
        externalLink
        internalLink {
          ...StandardPageReference
          ...SubPageReference
          ...CircularExamplePageReference
          ...ResourcePageReference
          ...WebflowPageReference
        }
      }
    }
  }
`;

const Page = ({ data, location, pageContext }) => {
  const canonicalSelfReference = `${vars.baseDomain}${location.pathname}`;
  const pageUrl = location.pathname;
  const pageData = data.standardPage;
  const pageValidation = validateStandardPageData(pageData);

  const footerData = getFooterData(pageContext.locale);

  const pageVideoMeta = pageData.videoMeta
    ? {
        name: pageData.videoMeta.videoTitle || 'Video',
        description:
          pageData.videoMeta.videoDescription?.videoDescription || undefined,
        thumbnailUrl: pageData.videoMeta.videoThumbnailUrl,
        uploadDate: pageData.videoMeta.videoUploadDate || undefined, // ISO 8601
        duration: pageData.videoMeta.videoDuration || undefined, // ISO 8601
        embedUrl: pageData.videoMeta.videoEmbedUrl,
      }
    : null;

  if (pageValidation.isValid) {
    const pageDefaultImages = data.pageDefaultImages;
    const pageColour = getPageColour();

    const transformedTags = pageContext.topics;

    // Use localeLinks from context
    const { localeLinks, standardPageLocales } = pageContext;

    const hreflangUrls = getHrefLangUrls(
      location.pathname,
      pageContext.locale,
      localeLinks
    );

    const navbarLocaleLinks = createNavbarLocaleLinks(
      standardPageLocales,
      pageContext.enabledLocaleCodes
    );

    // Podcast data
    let mediaSrc;
    const simplecastData = data.allSimplecastPodcastEpisode?.nodes;
    if (pageContext.pageType === 'podcast') {
      if (pageData.mediaSrc?.includes('simplecast')) {
        mediaSrc = getPodcastEmbedLink(pageData.mediaSrc, simplecastData);
      } else
        console.warn(
          `Podcast page rendered without masthead embed Media URL provided: ${pageData.title} [${pageContext.locale}]`
        );
    } else {
      /* If not a podcast, mediaSrc likely to be a youtube link which gets passed to the component as is */
      mediaSrc = pageData.mediaSrc;
    }

    // Tooltip data
    const { termsForTooltip } = data.contentfulGlossaryManager;

    const rawTooltipData = {
      termsForTooltip,
      shouldDisplayTextTooltips: pageData.shouldDisplayTextTooltips,
      usedTooltipKeywords: [],
    };

    // Flexible content data handling
    const transformedFlexibleContent = pageData.flexibleContent
      ? transformFlexibleContent({
          flexibleContent: pageData.flexibleContent,
          localeCode: pageContext.locale,
          staticTranslations: pageContext.staticTranslations,
          pageColour,
          simplecastData,
          rawTooltipData,
          currentEntryId: pageContext.id,
        })
      : null;

    // Related content handling
    const transformedRelatedContent = pageData.relatedContent
      ? transformTileContent(
          pageData.relatedContent,
          pageContext.locale,
          pageContext.staticTranslations
        )
      : null;

    const videoMetaArray = [
      ...(pageVideoMeta ? [pageVideoMeta] : []),
      ...(transformedFlexibleContent?.videoMeta || []),
    ];

    const metaData = {
      title: pageData.title,
      metaTitle: pageData.metaTitle || pageData.listingTitle || pageData.title,
      metaDescription: pageData.metaDescription || pageData.listingDescription,
      metaImage: transformSocialStaticImages(
        pageData.socialShareImage,
        pageContext.mastheadData.mastheadBackgroundImage,
        pageDefaultImages
      ),
      socialCardTitle: pageData.listingTitle || pageData.title,
      socialCardDescription:
        pageData.listingDescription || pageData.metaDescription,
      canonicalLink: pageData.canonicalLink
        ? pageData.canonicalLink
        : canonicalSelfReference,
      localeCode: pageContext.locale,
      videoMetaData: videoMetaArray,
    };

    const gtmDataLayerVars = {
      pageId: pageContext.contentfulId,
      event: 'Page View',
      pageType: `standard page${
        pageContext.pageType ? ` - ${pageContext.pageType}` : null
      }`,
      lastUpdated: pageData.updatedAt,
      topics: transformedTags,
      geography: pageData.geography,
    };

    const hasPaywall = pageData?.earlyAccessCode?.earlyAccessCode;

    return (
      <Layout
        footerData={footerData}
        pageType={pageContext.pageType}
        metaData={metaData}
        gtmDataLayerVars={gtmDataLayerVars}
        hreflangUrls={hreflangUrls}
        globals={pageContext?.globals ? pageContext.globals : null}
        navbarLocaleLinks={navbarLocaleLinks}
        pageUrl={pageUrl ? pageUrl : null}
      >
        <Masthead hasSecondaryNav={false} />

        <StyledWrapper>
          <div className="main-container">
            <div className="main-content-container" role="main">
              <IndentWrapper
                openingParagraph={
                  pageData.openingParagraph
                    ? pageData.openingParagraph.openingParagraph
                    : null
                }
                mediaSrc={mediaSrc || null}
                pageColour={pageColour}
              />

              {transformedFlexibleContent && (
                <FlexibleContentHandler data={transformedFlexibleContent} />
              )}

              {location.pathname === '/newsletter-sign-up' && (
                <SectionLayout displaySeparator={false}>
                  <MailchimpSignUpForm />
                </SectionLayout>
              )}

              <div
                id="hotjar_feedback_widget"
                style={{ paddingTop: '2.4rem' }}
              />

              {transformedRelatedContent && (
                <SectionLayout>
                  <TileBlock
                    title={Translate('You may also like', true)}
                    description={null}
                    cta={null}
                    visibleTilesCount={
                      transformedRelatedContent.visibleTilesCount
                    }
                    tiles={transformedRelatedContent}
                    colour={pageColour}
                  />
                </SectionLayout>
              )}

              {transformedTags && (
                <SectionLayout>
                  <TagList tags={transformedTags} />
                </SectionLayout>
              )}
            </div>
          </div>
        </StyledWrapper>
        {hasPaywall && (
          <Paywall
            earlyAccessCode={pageData?.earlyAccessCode?.earlyAccessCode}
            publicLiveDate={pageData.publicLiveDate}
          />
        )}
      </Layout>
    );
  } else {
    return (
      <ErrorLayout footerData={footerData} errors={pageValidation.errors} />
    );
  }
};

export default Page;
